import type {
  InventoryResource,
  Snapshot,
  AzureDisk,
  RestoreAccount,
} from '@repo/api-gw-sdk';
import { Provider } from '@repo/api-gw-sdk';

import { useEnvironment } from '@/contexts/useEnvironment';
import type { BackupVault } from '@/data/vaults/backupVault';

import { CrossCloudInstanceAdapter } from './crossCloudInstanceAdapter';
import { CrossCloudVolumeAdapter } from './crossCloudVolumeAdapter';
import { SelectTargetAccountStep } from './steps/selectTargetAccount';

import type { Step } from '../wizard/Step';
import { Wizard } from '../wizard/Wizard';

export interface AzureDiskConfigState {
  diskSettings: AzureDisk;
  keepOriginalTags: boolean;
  keepOriginalSettings: boolean;
  initialTags: Record<string, string>;
  tags: Record<string, string>;
}

export interface RestoreAzureVMState {
  resourceId: string;
  snapshot: Snapshot;
  restoreToTime?: Date;
  vault?: BackupVault;
  isPiTR: boolean;
  supportedCloudProviders: Provider[];
  restoreAccount?: RestoreAccount;
  resourceGroup?: string;
  location?: string;
  vmName?: string;
  originalVMSize?: string;
  vmSize?: string;
  keepOriginalVMSize: boolean;
  networkInterface?: string;
  virtualNetworkName?: string;
  newVirtualNetworkName?: string;
  subnetName?: string;
  newSubnetName?: string;
  keepOriginalTags: boolean;
  startInstanceAfterRestore: boolean;
  initialTags: Record<string, string>;
  tags: Record<string, string>;
  disksState: AzureDiskConfigState[];
  initialDiskStates: AzureDiskConfigState[];
  sourceProvider: Provider;
  sourceRegion: string;
}

const restoreAzureVMInstanceFlow: Step<RestoreAzureVMState> = {
  id: 'select_target_account_step',
  Component: SelectTargetAccountStep,
  next: [
    {
      id: 'azure_vm_configuration',
      Component: CrossCloudInstanceAdapter,
      next: [
        {
          id: 'disk_configuration',
          Component: CrossCloudVolumeAdapter,
        },
      ],
    },
  ],
};

export const RestoreAzureVMWizard = ({
  resource,
  snapshot,
  restoreToTime,
  vault,
  isPiTR = false,
  onClose,
  onBack,
}: {
  resource: InventoryResource;
  snapshot: Snapshot;
  restoreToTime?: Date;
  vault?: BackupVault;
  isPiTR?: boolean;
  onClose: () => void;
  onBack: () => void;
}) => {
  const { isDemo, isDev } = useEnvironment();

  const diskStates =
    snapshot.resource?.properties?.azureVm?.disks?.map((disk) => ({
      diskSettings: disk,
      diskId: disk.providerDiskId ?? '',
      diskName: disk.settings.name ?? '',
      diskType: disk.settings.type ?? '',
      diskTier: disk.settings.tier ?? '',
      diskSizeBytes: disk.settings.sizeBytes ?? 0,
      diskHyperVGeneration: disk.settings.hyperVGeneration ?? '',
      keepOriginalTags: true,
      keepOriginalSettings: true,
      initialTags: { ...disk.settings.tags },
      tags: {},
    })) ?? [];

  return (
    <Wizard<RestoreAzureVMState>
      onAbort={(origin) => (origin === 'back' ? onBack() : onClose())}
      onFinish={async () => true}
      flow={[restoreAzureVMInstanceFlow]}
      initialState={{
        snapshot,
        resourceId: snapshot.resourceId,
        restoreToTime,
        vault,
        isPiTR,
        supportedCloudProviders:
          isDemo || isDev
            ? [Provider.Aws, Provider.Azure, Provider.Gcp]
            : [Provider.Azure],
        keepOriginalTags: true,
        keepOriginalVMSize: true,
        startInstanceAfterRestore: true,
        vmName: snapshot.resource?.properties?.azureVm?.name ?? '',
        vmSize: snapshot.resource?.properties?.azureVm?.sizeBytes,
        originalVMSize: snapshot.resource?.properties?.azureVm?.sizeBytes,
        initialTags: snapshot.resource?.tags || {},
        tags: snapshot.resource?.tags || {},
        initialDiskStates: diskStates,
        disksState: diskStates,
        location: resource.region,
        sourceProvider: resource.cloudProvider,
        sourceRegion: resource.region,
      }}
      title={
        snapshot.supportPiTR ? 'Restore database' : 'Restore virtual machine'
      }
      stepperLabels={[
        'Restore account',
        'Instance configuration',
        'Disk configuration',
      ]}
    />
  );
};
